import { useEffect, useRef } from 'react';

export default function Hero() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elements = [titleRef.current, subRef.current, ctaRef.current, imgRef.current];
    elements.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(28px)';
      setTimeout(() => {
        if (!el) return;
        el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 200 + i * 180);
    });
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0F4A2B]"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20green%20luxury%20law%20office%20interior%20with%20marble%20floor%2C%20leather%20armchairs%2C%20bookshelves%20with%20legal%20volumes%2C%20warm%20gold%20lighting%2C%20sophisticated%20elegant%20atmosphere%2C%20Brazilian%20high%20end%20corporate%20setting%2C%20abstract%20bokeh%20background%2C%20deep%20forest%20green%20walls%20with%20gold%20accents%2C%20premium%20minimalist%20design&width=1920&height=1080&seq=hero001&orientation=landscape"
          alt="Escritório de advocacia premium"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C3A22]/95 via-[#0F4A2B]/85 to-[#0F4A2B]/40"></div>
      </div>

      {/* Gold top bar accent */}
      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#C7A76C] to-transparent z-10"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="flex flex-col gap-7">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#C7A76C]/15 border border-[#C7A76C]/30 text-[#D6BA85] text-xs font-sans font-medium px-4 py-2 rounded-full w-fit tracking-widest uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C7A76C] inline-block"></span>
              Advocacia &amp; Consultoria Especializada
            </div>

            <h1 ref={titleRef} className="font-serif text-4xl lg:text-6xl text-white font-semibold leading-tight">
              Resolva seu inventário com{' '}
              <span className="text-[#C7A76C] italic">segurança</span>{' '}
              e agilidade
            </h1>

            <p ref={subRef} className="font-sans text-white/70 text-base lg:text-lg leading-relaxed max-w-lg">
              A burocracia da herança não precisa ser um fardo. Cuidamos de todo o processo de
              inventário e regularização de imóveis para que você tenha tranquilidade e segurança
              jurídica em cada etapa.
            </p>

            <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/5511958814422?text=Olá%2C%20gostaria%20de%20mais%20informações%20sobre%20inventário"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-center gap-2.5 bg-[#C7A76C] hover:bg-[#D6BA85] text-[#0C3A22] font-sans font-semibold px-8 py-4 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Fale com um especialista
              </a>
              <button
                onClick={() => document.querySelector('#sobre')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center gap-2 border border-white/30 hover:border-[#C7A76C] text-white/80 hover:text-[#C7A76C] font-sans font-medium px-8 py-4 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap text-base"
              >
                Conheça o Dr. Bruno Castro
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 pt-4 border-t border-white/10">
              {[
                { icon: 'ri-award-line', label: 'OAB Regularizado' },
                { icon: 'ri-shield-check-line', label: 'Sigilo Garantido' },
                { icon: 'ri-time-line', label: 'Resposta Rápida' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-[#C7A76C] text-lg`}></i>
                  </div>
                  <span className="text-white/60 text-xs font-sans">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Lawyer Image */}
          <div ref={imgRef} className="hidden lg:flex justify-end items-end">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#C7A76C]/10 rounded-2xl blur-xl"></div>
              <div className="relative w-[420px] h-[580px] rounded-2xl overflow-hidden border border-[#C7A76C]/20">
                <img
                  src="https://storage.readdy-site.link/project_files/66d90ef2-528e-4451-823d-a1eab56ea0b6/fdd4f3d6-4b9c-4f94-91ae-81c82f88be55_DSC_6246.jpg?v=b85b9e6213c17d7edd09a6d8542688f2"
                  alt="Dr. Bruno Castro - Advogado especialista em inventário"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C3A22]/60 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-serif text-white text-xl font-semibold">Dr. Bruno Castro</p>
                  <p className="font-sans text-[#D6BA85] text-sm mt-1">Advogado Especialista em Inventário e Direito Imobiliário</p>
                </div>
              </div>
              {/* Gold accent decoration */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#C7A76C]/30 rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/40 text-xs font-sans tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-[#C7A76C]"></i>
      </div>
    </section>
  );
}
